
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchAgentByElevenLabsId, updateAgent, fetchElevenLabsAgentDetails } from "@/services/admin/agentService";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AgentForm } from "./AgentForm";
import { AgentVariables } from "./AgentVariables";
import { AgentElevenLabsInfo } from "./AgentElevenLabsInfo";
import { AgentVersions } from "./AgentVersions";
import { Agent, AgentFormData } from "@/types/admin";

interface AgentDetailSheetProps {
  elevenlabsAgentId: string;
  elevenLabsDetails: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}

export const AgentDetailSheet = ({ elevenlabsAgentId, elevenLabsDetails: initialDetails, open, onOpenChange, onClose }: AgentDetailSheetProps) => {
  const [activeTab, setActiveTab] = useState("details");
  const [elevenLabsDetails, setElevenLabsDetails] = useState<any>(initialDetails);
  const [isRefreshingElevenLabs, setIsRefreshingElevenLabs] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const { 
    data: agentWithVariables,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["agent-by-elevenlabs", elevenlabsAgentId],
    queryFn: () => fetchAgentByElevenLabsId(elevenlabsAgentId),
    enabled: open,
  });

  const updateAgentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AgentFormData> }) => 
      updateAgent(id, data),
    onSuccess: async (result) => {
      setUpdateError(null);
      if (result.error && result.error.elevenLabsError) {
        // Handle case where database updated but ElevenLabs failed
        setUpdateError(`Database updated but ElevenLabs update failed: ${result.error.elevenLabsError}`);
        toast({
          title: "Partial update",
          description: `Agent updated in database but ElevenLabs update failed: ${result.error.elevenLabsError}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Agent updated",
          description: "The agent has been updated successfully in both database and ElevenLabs.",
        });
      }
      
      // Refresh ElevenLabs details after update
      await refreshElevenLabsDetails();
      
      // Refresh local database data
      refetch();
    },
    onError: (error) => {
      setUpdateError(error instanceof Error ? error.message : "Unknown error updating agent");
      toast({
        title: "Error",
        description: `Failed to update agent: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    },
  });

  const refreshElevenLabsDetails = async () => {
    if (!elevenlabsAgentId) return;
    
    setIsRefreshingElevenLabs(true);
    try {
      const result = await fetchElevenLabsAgentDetails(elevenlabsAgentId);
      if (result.success && result.agent) {
        setElevenLabsDetails(result.agent);
        toast({
          title: "ElevenLabs details refreshed",
          description: "Successfully retrieved the latest agent details from ElevenLabs.",
        });
      } else {
        setUpdateError(result.error || "Failed to refresh ElevenLabs agent details");
        toast({
          title: "Error",
          description: result.error || "Failed to refresh ElevenLabs agent details.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setUpdateError(error instanceof Error ? error.message : "Unknown error refreshing ElevenLabs details");
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Unknown error refreshing ElevenLabs details",
        variant: "destructive",
      });
    } finally {
      setIsRefreshingElevenLabs(false);
    }
  };

  const handleUpdateAgent = (data: AgentFormData) => {
    if (agentWithVariables?.id) {
      setUpdateError(null);
      updateAgentMutation.mutate({ id: agentWithVariables.id, data });
    }
  };

  const handleSheetOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setTimeout(() => {
        setActiveTab("details");
        setUpdateError(null);
        onClose();
      }, 300);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleSheetOpenChange}>
      <SheetContent className="w-[60%] max-w-full flex flex-col p-0">
        <SheetHeader className="p-6 border-b">
          <SheetTitle>Agent: {elevenLabsDetails?.name || "Loading..."}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="p-4 text-red-500 bg-red-50 rounded-md m-6">
              Error loading agent details: {error instanceof Error ? error.message : "Unknown error"}
            </div>
          ) : agentWithVariables ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
              <TabsList className="grid grid-cols-4 w-full px-6 pt-6">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="variables">Variables</TabsTrigger>
                <TabsTrigger value="elevenlabs">ElevenLabs</TabsTrigger>
                <TabsTrigger value="versions">Versions</TabsTrigger>
              </TabsList>

              {updateError && (
                <div className="px-6 pt-3">
                  <div className="p-3 text-red-500 bg-red-50 rounded-md text-sm">
                    {updateError}
                  </div>
                </div>
              )}

              <ScrollArea className="flex-1 p-6">
                <TabsContent value="details" className="space-y-4 mt-0">
                  <AgentForm 
                    agent={agentWithVariables} 
                    onSubmit={handleUpdateAgent}
                    isUpdating={updateAgentMutation.isPending || isRefreshingElevenLabs}
                    hideSubmitButton={true}
                  />
                </TabsContent>

                <TabsContent value="variables" className="space-y-4 mt-0">
                  <AgentVariables 
                    agentId={agentWithVariables.id} 
                    variables={agentWithVariables.variables || []}
                    onSuccess={() => refetch()}
                  />
                </TabsContent>

                <TabsContent value="elevenlabs" className="space-y-4 mt-0">
                  <AgentElevenLabsInfo 
                    agent={agentWithVariables}
                    onUpdate={() => {
                      refetch();
                      refreshElevenLabsDetails();
                    }}
                    elevenLabsDetails={elevenLabsDetails}
                  />
                </TabsContent>

                <TabsContent value="versions" className="space-y-4 mt-0">
                  <AgentVersions 
                    agentId={agentWithVariables.id}
                    onVersionRestored={() => {
                      refetch();
                      refreshElevenLabsDetails();
                    }}
                  />
                </TabsContent>
              </ScrollArea>
            </Tabs>
          ) : (
            <div className="flex justify-center p-8">
              <p>No agent data found</p>
            </div>
          )}
        </div>

        {agentWithVariables && activeTab === "details" && (
          <div className="p-6 border-t bg-background w-full sticky bottom-0 flex justify-end gap-2">
            <Button 
              variant="outline"
              onClick={onClose} 
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (agentWithVariables) {
                  // Get the form data from the form component and submit it
                  const formElement = document.querySelector('form');
                  if (formElement) {
                    formElement.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                }
              }}
              disabled={updateAgentMutation.isPending || isRefreshingElevenLabs}
            >
              {updateAgentMutation.isPending || isRefreshingElevenLabs ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
